import { BaseDrawArgs, getMiddleCenterBias, measureTextCached } from '@glideapps/glide-data-grid'

interface CornerRadius {
  tl: number
  tr: number
  bl: number
  br: number
}

export function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number | CornerRadius
) {
  if (width <= 0 || height <= 0) return
  if (typeof radius === 'number' && radius <= 0) {
    ctx.rect(x, y, width, height)
    return
  }
  if (typeof radius === 'number') {
    radius = { tl: radius, tr: radius, br: radius, bl: radius }
  }

  // restrict radius to a reasonable max
  radius = {
    tl: Math.min(radius.tl, height / 2, width / 2),
    tr: Math.min(radius.tr, height / 2, width / 2),
    bl: Math.min(radius.bl, height / 2, width / 2),
    br: Math.min(radius.br, height / 2, width / 2),
  }

  radius.tl = Math.max(0, radius.tl)
  radius.tr = Math.max(0, radius.tr)
  radius.br = Math.max(0, radius.br)
  radius.bl = Math.max(0, radius.bl)

  ctx.moveTo(x + radius.tl, y)
  ctx.arcTo(x + width, y, x + width, y + radius.tr, radius.tr)
  ctx.arcTo(x + width, y + height, x + width - radius.br, y + height, radius.br)
  ctx.arcTo(x, y + height, x, y + height - radius.bl, radius.bl)
  ctx.arcTo(x, y, x + radius.tl, y, radius.tl)
}

const itemMargin = 4

const getThemeColors = (themeName: string, theme: any) => {
  const isDarkMode = themeName === 'dark'

  return {
    backgroundColor: isDarkMode ? '#4a4a4a' : theme.bgBubble,
    backgroundSelectedColor: isDarkMode ? '#4a4a4a' : theme.bgBubbleSelected,
    textColor: isDarkMode ? '#fff' : theme.textBubble,
  }
}

export function drawBubbles(args: BaseDrawArgs, data: readonly string[], themeName: string) {
  const { rect, theme, ctx, highlighted } = args
  const { x, y, width: w, height: h } = rect
  const bubbleHeight = 20
  const bubblePad = 8
  const bubbleMargin = itemMargin
  let renderX = x + theme.cellHorizontalPadding

  const { backgroundColor, backgroundSelectedColor, textColor } = getThemeColors(themeName, theme)

  const renderBoxes: { x: number; width: number }[] = []
  for (const s of data) {
    if (renderX > x + w) break
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const textWidth = measureTextCached(s, ctx, theme.baseFontFull).width
    renderBoxes.push({
      x: renderX,
      width: textWidth,
    })

    renderX += textWidth + bubblePad * 2 + bubbleMargin
  }

  ctx.beginPath()
  for (const rectInfo of renderBoxes) {
    roundedRect(
      ctx,
      rectInfo.x,
      y + (h - bubbleHeight) / 2,
      rectInfo.width + bubblePad * 2,
      bubbleHeight,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      theme.roundingRadius ?? bubbleHeight / 2
    )
  }
  ctx.fillStyle = highlighted ? backgroundSelectedColor : backgroundColor
  ctx.fill()

  for (const [i, rectInfo] of renderBoxes.entries()) {
    ctx.beginPath()
    ctx.fillStyle = textColor
    ctx.fillText(data[i], rectInfo.x + bubblePad, y + h / 2 + getMiddleCenterBias(ctx, theme))
  }
}
